import { FieldValues, useForm } from 'react-hook-form';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import OnboardingTabHeader from 'pages/dashboard/components/OnboardingTabHeader';
import { MailIcon, PhoneIcon, InfoCircleSolidIcon } from 'components/icons';
import Link from 'components/Link';
import ErrorMessage from 'components/Inputs/components/ErrorMessage';
import {
  Input,
  Select,
  PhoneInput,
  EmailInput,
  PostalCode,
} from 'components/Inputs';
import IdNumberInput from './components/IdNumberInput';
import DoingBusinessAs from './components/DoingBusinessAs';
import CompanyImages from './components/CompanyImages';
import AddressState from 'components/AddressState';
import Alert, { AlertTypes } from 'components/Alert';
import { useAppDispatch, useAppSelector } from 'hooks';
import { getTheme } from 'theme/selectors';
import { setStep, setRedirectToDashboard } from 'pages/Profile/profileSlice';
import { setIsLoading } from 'pages/dashboard/dashboardSlice';
import {
  getSupportedCountries,
  hasPermission,
  getAllowEditPhone,
} from 'pages/dashboard/selectors';
import {
  getIsCapitalProductActive,
  getShowErrors,
} from 'pages/dashboard/components/Progress/selectors';

import {
  setFormUpdateState,
  setIsSaving,
  setShowDoingBusinessAs,
} from './businessInfoSlice';
import {
  updateBusinessInfo,
  checkBusinessInfoProgress,
  fetchBusinessDocumentTypes,
  fetchBusinessLegalEntities,
} from './thunks';
import { getIsFormUpdate, getBusinessInfo, getIdTypes } from './selectors';
import { CountryISOCodes } from 'common/countries';
import path from 'common/path';
import { onboardingSteps } from 'pages/Profile/utils';
import { Permissions } from 'entities/dashboard';
import PermissionSection from 'components/permission/PermissionSection';
import {
  getCityLabel,
  getPostalCodeLabel,
  handleAddressOnSubmit,
} from 'utils/address';
import OnboardingTabActions from '../OnboardingTabActions';

const BusinessInformation = () => {
  const { t } = useTranslation('dashboard');
  const dispatch = useAppDispatch();

  const isFormUpdate = useAppSelector(getIsFormUpdate);
  const countries = useAppSelector(getSupportedCountries);
  const businessInfo = useAppSelector(getBusinessInfo);
  const { textColor } = useAppSelector(getTheme);
  const showErrors: any = useAppSelector(getShowErrors);
  const { idTypes, legalEntityTypes, idTypesCountryId } =
    useAppSelector(getIdTypes);
  const isCapitalProductActive = useAppSelector(getIsCapitalProductActive);
  const onboardingPermissions = useAppSelector((state) =>
    hasPermission(state, Permissions.onboardingBusiness),
  );
  const allowEditPhone = useAppSelector(getAllowEditPhone);

  const showError = useMemo(
    () => showErrors.businessInfo && onboardingPermissions.edit,
    [showErrors.businessInfo, onboardingPermissions.edit],
  );

  const { register, reset, setValue, getValues, watch, control } =
    useForm<FieldValues>({
      defaultValues: businessInfo,
    });

  const [alertIsOpened, setAlertIsOpened] = useState(true);
  const prevDocTypeRef = useRef(businessInfo.businessesDocumentsTypesId);

  const [
    businessLegalName,
    businessesLegalEntityFormTypesId,
    countryIsocode,
    addressLine1,
    addressLine2,
    city,
    postalCode,
    stateOrProvince,
    centralIndexKey,
    phone,
    businessEmailAddress,
    primaryURL,
    contactEmailAddress,
    contactTelephoneNumber,
    doingBusinessAs,
    businessesDocumentsTypesId,
    idNumber,
    referenceNumber,
    applicantPhone,
  ] = watch([
    'businessLegalName',
    'businessesLegalEntityFormTypesId',
    'countryIsocode',
    'addressLine1',
    'addressLine2',
    'city',
    'postalCode',
    'stateOrProvince',
    'centralIndexKey',
    'phone',
    'businessEmailAddress',
    'primaryURL',
    'contactEmailAddress',
    'contactTelephoneNumber',
    'doingBusinessAs',
    'businessesDocumentsTypesId',
    'idNumber',
    'referenceNumber',
    'applicantPhone',
  ]);

  const hasChanges = useMemo(
    () =>
      businessLegalName !== businessInfo.businessLegalName ||
      referenceNumber !== businessInfo.referenceNumber ||
      businessesLegalEntityFormTypesId !==
        businessInfo.businessesLegalEntityFormTypesId ||
      countryIsocode !== businessInfo.countryIsocode ||
      addressLine1 !== businessInfo.addressLine1 ||
      addressLine2 !== businessInfo.addressLine2 ||
      city !== businessInfo.city ||
      postalCode !== businessInfo.postalCode ||
      stateOrProvince !== businessInfo.stateOrProvince ||
      centralIndexKey !== businessInfo.centralIndexKey ||
      phone !== businessInfo.phone ||
      businessEmailAddress !== businessInfo.businessEmailAddress ||
      primaryURL !== businessInfo.primaryURL ||
      contactEmailAddress !== businessInfo.contactEmailAddress ||
      contactTelephoneNumber !== businessInfo.contactTelephoneNumber ||
      doingBusinessAs !== businessInfo.doingBusinessAs ||
      businessesDocumentsTypesId !== businessInfo.businessesDocumentsTypesId ||
      idNumber !== businessInfo.idNumber ||
      applicantPhone !== businessInfo.applicantPhone,
    [
      businessLegalName,
      referenceNumber,
      businessesLegalEntityFormTypesId,
      countryIsocode,
      addressLine1,
      addressLine2,
      city,
      postalCode,
      stateOrProvince,
      centralIndexKey,
      phone,
      businessEmailAddress,
      primaryURL,
      contactEmailAddress,
      contactTelephoneNumber,
      doingBusinessAs,
      businessesDocumentsTypesId,
      idNumber,
      applicantPhone,
      businessInfo,
    ],
  );

  const selectedCountry = countries.find(
    (c) => c.iSOCode === (countryIsocode || CountryISOCodes.USA),
  );

  const handleSave = async () => {
    if (!onboardingPermissions.edit) {
      return null;
    }

    dispatch(setIsSaving(true));

    const data = handleAddressOnSubmit(getValues());

    data.alpha2Code = selectedCountry?.alpha2Code;

    const result = await dispatch(
      updateBusinessInfo({ ...data, dbaFile: businessInfo.dbaFile }),
    );

    reset(data);

    await dispatch(checkBusinessInfoProgress(data));

    dispatch(setIsSaving(false));

    return result;
  };

  const handleCancel = () => {
    if (hasChanges) {
      reset(businessInfo);
    }
  };

  useEffect(() => {
    return () => {
      handleSave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessInfo.dbaFile]);

  useEffect(() => {
    if (!isFormUpdate) {
      if (businessInfo.doingBusinessAs) {
        dispatch(setShowDoingBusinessAs(true));
      }
      reset(businessInfo);
      dispatch(setFormUpdateState());
    }
  }, [businessInfo, isFormUpdate, reset, dispatch]);

  const handleAlertClick = () => {
    setAlertIsOpened(!alertIsOpened);
  };

  const handleChangeBusiness = () => {
    if (onboardingPermissions.edit) {
      dispatch(setStep(onboardingSteps.business));
      dispatch(setRedirectToDashboard(false));
      dispatch(setIsLoading(true));
    }
  };

  useEffect(() => {
    if (idTypesCountryId !== countryIsocode) {
      dispatch(fetchBusinessDocumentTypes(countryIsocode));
      dispatch(fetchBusinessLegalEntities(countryIsocode));
    }
  }, [countryIsocode, idTypesCountryId, dispatch]);

  const selectedLegalEntity = legalEntityTypes.find(
    (entity: any) => entity.value === businessesLegalEntityFormTypesId,
  );

  const selectedDocumentType = idTypes.find(
    (type) => type.businessesDocumentsTypesId === businessesDocumentsTypesId,
  );

  const HandleChangeIdType = (selected) => {
    if (selected.businessesDocumentsTypesId !== prevDocTypeRef.current) {
      setValue('idNumber', '');
    }

    prevDocTypeRef.current = selected.businessesDocumentsTypesId;
  };

  return (
    <div>
      <PermissionSection
        permission={Permissions.onboardingBusiness}
        showMessage
      >
        <>
          <OnboardingTabHeader
            title={t('dashboard:businessInfo.title')}
            subTitle={t('dashboard:businessInfo.subTitle')}
            onSave={isCapitalProductActive ? handleSave : undefined}
            onCancel={isCapitalProductActive ? handleCancel : undefined}
            isActionsDisabled={!hasChanges}
          />

          <form className="p-6">
            <div className="border-b border-gray-200 pb-5">
              <div className="flex items-end -mx-4.5">
                <Input
                  register={register}
                  label={t('common:fields.businessLegalName.label')}
                  name="businessLegalName"
                  className="w-full mx-4.5"
                  readOnly={
                    (businessInfo.businessLegalName &&
                      businessInfo.dunsnumber) ||
                    !onboardingPermissions.edit
                  }
                  error={showError && !businessLegalName}
                />
                <Input
                  register={register}
                  label={t('dashboard:fields.referenceNumber.label')}
                  info={t('dashboard:fields.referenceNumber.info')}
                  name="referenceNumber"
                  className="w-full mx-4.5"
                  readOnly={!onboardingPermissions.edit}
                  maxLength={10}
                  optional
                />
                {alertIsOpened ? (
                  <Alert
                    type={AlertTypes.info}
                    title={
                      <span
                        style={{
                          color: textColor.secondery.base,
                        }}
                      >
                        {t('dashboard:alerts.changeCompany.title')}
                        <div className="inline-block">
                          <PermissionSection
                            permission={Permissions.onboardingBusiness}
                            showPopover
                            edit
                          >
                            <Link
                              to={path.profile}
                              state={{ checkStep: false }}
                              styles={{
                                color: textColor.secondery.base,
                                ':hover': {
                                  color: textColor.primary.hover,
                                },
                              }}
                              className="ml-3"
                              onClick={handleChangeBusiness}
                              disabled={!onboardingPermissions.edit}
                            >
                              {t('dashboard:alerts.changeCompany.link')}
                            </Link>
                          </PermissionSection>
                        </div>
                      </span>
                    }
                    className="mt-5 mx-4.5 flex-shrink-0"
                    paddingClass="p-11px"
                    onClose={handleAlertClick}
                    withClose
                  />
                ) : (
                  <button
                    onClick={handleAlertClick}
                    className="rounded-md p-11px mt-5 mx-4.5 flex-shrink-0 text-blue-400 bg-blue-50"
                  >
                    <InfoCircleSolidIcon />
                  </button>
                )}
              </div>
              {showError && !businessLegalName && (
                <ErrorMessage
                  error={{
                    message: t(
                      'common:fields.businessLegalName.validation.required',
                    ),
                  }}
                />
              )}
            </div>

            <CompanyImages />

            <div className="flex flex-wrap -mx-8">
              <div className="w-1/2 px-8">
                <Select
                  label={t('common:fields.country.label')}
                  name="countryIsocode"
                  setValue={setValue}
                  value={selectedCountry}
                  className="w-full mt-5"
                  options={countries}
                  disabled={
                    (businessInfo.countryIsocode && businessInfo.dunsnumber) ||
                    !onboardingPermissions.edit
                  }
                  error={
                    showError &&
                    !countryIsocode && {
                      message: t('common:fields.country.validation.required'),
                    }
                  }
                  withIcon
                />
                <Select
                  label={t('dashboard:fields.entityForm.label')}
                  placeholder={t('dashboard:fields.entityForm.placeholder')}
                  name="businessesLegalEntityFormTypesId"
                  className="w-full mt-5"
                  options={legalEntityTypes}
                  setValue={setValue}
                  value={selectedLegalEntity}
                  disabled={
                    (businessInfo.businessesLegalEntityFormTypesId &&
                      businessInfo.dunsnumber) ||
                    !onboardingPermissions.edit
                  }
                  error={
                    showError &&
                    !selectedLegalEntity && {
                      message: t(
                        'dashboard:fields.entityForm.validation.required',
                      ),
                    }
                  }
                />
                <Input
                  register={register}
                  label={t('dashboard:fields.address.label')}
                  name="addressLine1"
                  className="w-full mt-5"
                  readOnly={!onboardingPermissions.edit}
                  error={
                    showError &&
                    !addressLine1 && {
                      message: t(
                        'dashboard:fields.address.validation.required',
                      ),
                    }
                  }
                />
                <Input
                  register={register}
                  label={t('common:fields.address2.label')}
                  name="addressLine2"
                  className="w-full mt-5"
                  readOnly={!onboardingPermissions.edit}
                  optional
                />
                <Input
                  register={register}
                  label={t(getCityLabel(countryIsocode))}
                  name="city"
                  className="w-full mt-5"
                  readOnly={
                    (businessInfo.city && businessInfo.dunsnumber) ||
                    !onboardingPermissions.edit
                  }
                  error={
                    showError &&
                    !city && {
                      message: t('common:fields.city.validation.required'),
                    }
                  }
                />
                <div className="w-full flex gap-6">
                  <PostalCode
                    register={register}
                    setValue={setValue}
                    label={t(getPostalCodeLabel(countryIsocode))}
                    name="postalCode"
                    className="w-full mt-5"
                    countryIsocode={countryIsocode}
                    validate
                    readOnly={
                      (businessInfo.postalCode && businessInfo.dunsnumber) ||
                      !onboardingPermissions.edit
                    }
                    error={
                      showError &&
                      !postalCode && {
                        message: t('common:fields.postal.validation.required'),
                      }
                    }
                  />
                  <AddressState
                    label={t('common:fields.province.label')}
                    name="stateOrProvince"
                    className="w-full mt-5"
                    countryISOCode={countryIsocode}
                    disabled={
                      (businessInfo.stateOrProvince &&
                        businessInfo.dunsnumber) ||
                      !onboardingPermissions.edit
                    }
                    register={register}
                    setValue={setValue}
                    value={stateOrProvince}
                    error={
                      showError &&
                      !stateOrProvince && {
                        message: t(
                          'common:fields.province.validation.required',
                        ),
                      }
                    }
                  />
                </div>
              </div>

              <div className="w-1/2 px-8">
                <Input
                  register={register}
                  label={t('dashboard:fields.centralIndexKey.label')}
                  readOnly={!onboardingPermissions.edit}
                  name="centralIndexKey"
                  className="w-full mt-5"
                  optional
                />
                <PhoneInput
                  label={t('dashboard:fields.phone.label')}
                  country={selectedCountry?.alpha2Code}
                  name="phone"
                  className="w-full mt-5"
                  control={control}
                  iconEnd={<PhoneIcon className="text-gray-400" />}
                  readOnly={!onboardingPermissions.edit}
                  error={
                    showError &&
                    !phone && {
                      message: t('dashboard:fields.phone.validation.required'),
                    }
                  }
                />
                <EmailInput
                  register={register}
                  setValue={setValue}
                  label={t('dashboard:fields.email.label')}
                  placeholder={t('dashboard:fields.email.placeholder')}
                  name="businessEmailAddress"
                  className="w-full mt-5"
                  iconEnd={<MailIcon className="text-gray-400" />}
                  readOnly={!onboardingPermissions.edit}
                  error={
                    showError &&
                    !businessEmailAddress && {
                      message: t('dashboard:fields.email.validation.required'),
                    }
                  }
                />
                <Input
                  register={register}
                  label={t('dashboard:fields.primaryURL.label')}
                  placeholder={t('dashboard:fields.primaryURL.placeholder')}
                  name="primaryURL"
                  className="w-full mt-5"
                  prefix="http://"
                  readOnly={!onboardingPermissions.edit}
                  error={
                    showError &&
                    !primaryURL && {
                      message: t(
                        'dashboard:fields.primaryURL.validation.required',
                      ),
                    }
                  }
                />

                <div className="bg-gray-50 rounded-[8px] p-4 w-full mt-6">
                  <div className="text-gray-900 font-medium text-sm">
                    {t('dashboard:businessInfo.support.title')}
                  </div>
                  <EmailInput
                    register={register}
                    setValue={setValue}
                    label={t('common:fields.email.label')}
                    placeholder={t('common:fields.email.placeholder')}
                    name="contactEmailAddress"
                    className="w-full mt-5"
                    iconEnd={<MailIcon className="text-gray-400" />}
                    readOnly={!onboardingPermissions.edit}
                    error={
                      showError &&
                      !contactEmailAddress && {
                        message: t('common:fields.email.validation.required'),
                      }
                    }
                  />
                  <PhoneInput
                    label={t('common:fields.phone.label')}
                    country={selectedCountry?.alpha2Code}
                    name="contactTelephoneNumber"
                    className="w-full mt-5"
                    control={control}
                    iconEnd={<PhoneIcon className="text-gray-400" />}
                    readOnly={!onboardingPermissions.edit}
                    error={
                      showError &&
                      !contactTelephoneNumber && {
                        message: t('common:fields.phone.validation.required'),
                      }
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex mt-16 gap-8">
              <Select
                label={t('dashboard:fields.idType.label')}
                placeholder={t('dashboard:fields.idType.placeholder')}
                name="businessesDocumentsTypesId"
                className="w-1/2"
                options={idTypes}
                setValue={setValue}
                value={selectedDocumentType}
                disabled={!onboardingPermissions.edit}
                onChange={HandleChangeIdType}
                error={
                  showError &&
                  !businessesDocumentsTypesId && {
                    message: t('dashboard:fields.idType.validation.required'),
                  }
                }
              />
              <IdNumberInput
                control={control}
                typeCode={selectedDocumentType?.typeCode}
                readOnly={!onboardingPermissions.edit}
                error={
                  showError &&
                  !idNumber && {
                    message: t('dashboard:fields.idNumber.validation.required'),
                  }
                }
              />
            </div>

            <DoingBusinessAs
              register={register}
              disabled={
                (businessInfo.doingBusinessAs && businessInfo.dunsnumber) ||
                !onboardingPermissions.edit
              }
              fileUploadDisabled={
                (businessInfo.dbaFile && businessInfo.dunsnumber) ||
                !onboardingPermissions.edit
              }
              error={doingBusinessAs && showError && !businessInfo.dbaFile}
            />

            <div className="border-b pb-5">
              <p className="heading-lg mt-16 mb-1">
                {t('dashboard:businessInfo.applicant.title')}
              </p>
              <p className="text-secondary">
                {t('dashboard:businessInfo.applicant.subTitle')}
              </p>
            </div>

            <div className="flex items-center -mx-4.5">
              <Input
                register={register}
                label={t('dashboard:businessInfo.applicant.name')}
                name="applicantName"
                className="w-full mx-4.5 mt-5"
                readOnly
              />

              <Input
                register={register}
                label={t('dashboard:businessInfo.applicant.role')}
                name="applicantRole"
                className="w-full mx-4.5 mt-5"
                readOnly
              />
            </div>

            <div className="flex items-center -mx-4.5">
              <Input
                register={register}
                label={t('dashboard:businessInfo.applicant.email')}
                name="applicantEmail"
                className="w-full mx-4.5 mt-5"
                readOnly
              />

              <PhoneInput
                control={control}
                label={t('dashboard:businessInfo.applicant.phone')}
                country={selectedCountry?.alpha2Code}
                name="applicantPhone"
                className="w-full mx-4.5 mt-5"
                readOnly={!allowEditPhone}
                error={
                  showError &&
                  !applicantPhone && {
                    message: t('common:fields.phone.validation.required'),
                  }
                }
              />
            </div>

            {isCapitalProductActive && (
              <div className="mt-6 flex justify-end">
                <OnboardingTabActions
                  onSave={handleSave}
                  onCancel={handleCancel}
                  isActionsDisabled={!hasChanges}
                />
              </div>
            )}
          </form>
        </>
      </PermissionSection>
    </div>
  );
};

export default BusinessInformation;
